// Pick modal — opens when a source/reference slot is clicked. A small image
// library with category tabs (Gallery / Favorites) + infinite scroll + upload;
// a pick fills the clicked slot via the same image-role helpers the docked
// gallery used. why → ADR-005 2.8.0 #1 (single source picker = this modal; it
// must be a real browsable library, not a flat capped grid).
import { store } from '../../core/store.js'
import { useImageAsSource, useImageAsReference } from './image-roles.js'
import { uploadImage } from '../../api/index.js'
import { showToast } from '../../utils/toast.js'
import { loadGallery } from '../gallery/index.js'
import { getFavorites } from '../me/favorites/index.js'

let _el = null
let _role = 'source'
let _tab = 'gallery' // 'gallery' | 'favorites'

function makeFd(file) {
  const fd = new FormData()
  fd.append('image', file)
  fd.append('source', _role)
  return fd
}

function applyPick(pick, origin) {
  if (_role === 'reference') useImageAsReference(pick, origin)
  else useImageAsSource(pick, origin)
  closePickModal()
}

// The active tab's images, normalized to a common {id,url,thumbnail,title} shape.
// Gallery = the paginated recommendation library (store.gallery.images);
// Favorites = the user's saved images (me/favorites, carries its own urls so it
// does NOT depend on what the gallery page happens to have loaded).
function tabImages() {
  if (_tab === 'favorites') {
    return getFavorites()
      .map(f => {
        const url = f.item_image_url || f.image_url || ''
        return { id: f.item_id || f.id, url, thumbnail: url, title: f.item_title || f.source_title || '' }
      })
      .filter(i => i.url)
  }
  return store.getState('gallery')?.images || []
}

function itemEl(img) {
  const thumb = img.thumbnail || img.thumbnail_url || img.main_image_url || img.url || ''
  const btn = document.createElement('button')
  btn.className = 'pick-item'
  btn.type = 'button'
  const im = document.createElement('img')
  im.src = thumb
  im.loading = 'lazy'
  im.alt = img.title || ''
  btn.appendChild(im)
  btn.addEventListener('click', () => applyPick({ url: img.url, id: img.id, title: img.title }, 'library'))
  return btn
}

// Paint the grid. `append` only adds the new tail (infinite scroll) so the
// scroll position and already-rendered thumbnails are preserved.
function fillGrid(images, append) {
  const grid = _el.querySelector('#pickGrid')
  if (!append) grid.innerHTML = ''
  if (!images.length && !append) {
    grid.innerHTML = `<p class="pick-empty">${
      _tab === 'favorites'
        ? 'No favorites yet — tap the heart on any image to save it here.'
        : 'Library is loading…'
    }</p>`
    return
  }
  const frag = document.createDocumentFragment()
  images.forEach(img => { if (img && (img.url || img.thumbnail)) frag.appendChild(itemEl(img)) })
  grid.appendChild(frag)
}

function renderTab() {
  const grid = _el.querySelector('#pickGrid')
  if (grid) grid.scrollTop = 0
  fillGrid(tabImages(), false)
}

// Infinite scroll — only the Gallery tab paginates (Favorites is a finite saved
// set). Loads the next page near the bottom and appends just the new images.
function onGridScroll() {
  if (_tab !== 'gallery') return
  const grid = _el.querySelector('#pickGrid')
  if (grid.scrollTop + grid.clientHeight < grid.scrollHeight - 220) return
  const g = store.getState('gallery')
  if (!g?.hasMore || g.loading) return
  const prevLen = (g.images || []).length
  loadGallery(true).then(() => {
    const imgs = store.getState('gallery')?.images || []
    if (imgs.length > prevLen) fillGrid(imgs.slice(prevLen), true)
  })
}

function setTab(tab) {
  _tab = tab
  _el.querySelectorAll('.pick-tab').forEach(t => t.classList.toggle('active', t.dataset.pickTab === tab))
  renderTab()
}

function ensureModal() {
  if (_el) return _el
  _el = document.createElement('div')
  _el.className = 'pick-scrim'
  _el.innerHTML = `
    <div class="pick-panel" role="dialog" aria-modal="true">
      <h3 class="pick-title" id="pickTitle">Pick an image</h3>
      <div class="pick-tabs" id="pickTabs">
        <button class="pick-tab active" data-pick-tab="gallery" type="button">Gallery</button>
        <button class="pick-tab" data-pick-tab="favorites" type="button">Favorites</button>
      </div>
      <div class="pick-grid" id="pickGrid"></div>
      <div class="pick-foot">
        <button class="pick-upload" id="pickUpload" type="button"><i class="ph ph-upload-simple" aria-hidden="true"></i> Upload new</button>
        <span class="pick-hint">or drag an image onto the slot</span>
      </div>
      <input type="file" id="pickFileInput" accept="image/*" style="display:none" />
      <button class="pick-x" id="pickClose" type="button" aria-label="Close"><i class="ph ph-x"></i></button>
    </div>`
  document.body.appendChild(_el)
  _el.addEventListener('click', (e) => { if (e.target === _el) closePickModal() })
  _el.querySelector('#pickClose').addEventListener('click', closePickModal)
  _el.querySelectorAll('.pick-tab').forEach(t => t.addEventListener('click', () => setTab(t.dataset.pickTab)))
  _el.querySelector('#pickGrid').addEventListener('scroll', onGridScroll)
  _el.querySelector('#pickUpload').addEventListener('click', () => _el.querySelector('#pickFileInput').click())
  _el.querySelector('#pickFileInput').addEventListener('change', async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      const asset = await uploadImage(makeFd(file))
      const url = asset?.public_url || asset?.url
      if (url) applyPick({ url, id: asset?.id }, 'asset')
    } catch {
      showToast('Upload failed', 'error')
    }
  })
  return _el
}

/** Open the picker targeting a slot. @param {'source'|'reference'} role */
export function openPickModal(role) {
  _role = role === 'reference' ? 'reference' : 'source'
  ensureModal()
  _el.querySelector('#pickTitle').textContent = _role === 'reference' ? 'Pick a reference image' : 'Pick a source image'
  setTab('gallery')
  // Cold open before the boot preload finished — fetch the first page, then paint.
  if (!(store.getState('gallery')?.images || []).length) {
    loadGallery().then(() => { if (_tab === 'gallery') renderTab() })
  }
  _el.classList.add('show')
}

export function closePickModal() {
  if (_el) _el.classList.remove('show')
}
